import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
} from '@angular/core';
import {
  DialogLoadingComponent,
  I2gDialogService,
  ToastService,
} from 'i2g-ng-ui';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { EMAIL_MAX_LENGTH } from '../../services/email/email.service';
import { CustomValidators } from 'ngx-custom-validators';
import { TrackingDialogNamesEnum } from '../../../tracking/tracking.data';
import {
  I2gNgTrackingActionBuilder,
  ITrackingAction,
  ITrackingObject,
} from 'i2g-ng-tracking';
import { take } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { AccountDataDispatcher } from '../../../account-data/account-data.dispatcher';
import { ErrorService } from '../../../error-reporting/error.service';
import { LoggerService } from 'i2g-ng-utils';
import { BehaviorSubject, Observable } from 'rxjs';
import { TrackingDispatcher } from '../../../tracking/tracking.dispatcher';
import { TrackingObjectBuilder } from '../../../tracking/tracking-object.builder';

@Component({
  selector: 'app-change-email-dialog',
  templateUrl: './change-email-dialog.component.html',
  styleUrls: ['./change-email-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChangeEmailDialogComponent implements OnInit {
  static isDismissible = true;

  @Input() trackingScreenName: string;

  continueAction: ITrackingAction;
  cancelAction: ITrackingAction;
  trackingObject: ITrackingObject;

  emailControl: FormControl;
  private _focusSubject: BehaviorSubject<any> = new BehaviorSubject<any>(true);
  focusableTrigger$: Observable<void> = this._focusSubject.asObservable();

  constructor(
    private _dialogService: I2gDialogService,
    private _formBuilder: FormBuilder,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder,
    private _translateService: TranslateService,
    private _accountDataDispatcher: AccountDataDispatcher,
    private _errorService: ErrorService,
    private _loggerService: LoggerService,
    private _trackingDispatcher: TrackingDispatcher,
    private _trackingObjectBuilder: TrackingObjectBuilder,
    private _toastService: ToastService
  ) {}

  ngOnInit() {
    this.emailControl = this._formBuilder.control('', [
      Validators.required,
      Validators.maxLength(EMAIL_MAX_LENGTH),
      CustomValidators.email,
    ]);

    this.continueAction = this._trackingActionBuilder.buildButtonTapped(
      'confirm_change',
      this.trackingScreenName
    );
    this.cancelAction = this._trackingActionBuilder.buildButtonTapped(
      'cancel',
      this.trackingScreenName
    );
    this.trackingObject = this._trackingObjectBuilder.buildDialog(
      TrackingDialogNamesEnum.settings_change_account_email
    );
  }

  onPrimaryButtonClicked() {
    if (this.emailControl.invalid) {
      return;
    }

    this._trackingDispatcher.trackEvent(
      this.trackingObject,
      this.continueAction
    );
    this._loggerService.log('saving account email info');

    this._dialogService.openDialog(DialogLoadingComponent);
    this._accountDataDispatcher
      .changeEmail(this.emailControl.value)
      .pipe(take(1))
      .subscribe(
        () => {
          this._dialogService.closeDialog();
          this._toastService.show(
            this._translateService.instant(
              'SETTINGS.CHANGE_EMAIL_DIALOG.SUCCESS_MESSAGE'
            )
          );
        },
        (error) => {
          const title = this._translateService.instant(
            'SETTINGS.ACCOUNT_DETAILS.CHANGE_EMAIL_ERROR.TITLE'
          );
          const body = this._translateService.instant(
            'SETTINGS.ACCOUNT_DETAILS.CHANGE_EMAIL_ERROR.MESSAGE'
          );
          const secondaryButtonText = this._translateService.instant(
            'ACTION.DISMISS'
          );

          this._dialogService.openAlertDialog({
            title,
            body,
            secondaryButtonText,
          });

          this._errorService.alertSystem(error);
        }
      );
  }

  onSecondaryButtonClicked() {
    this._dialogService.closeDialog();
    this._trackingDispatcher.trackEvent(this.trackingObject, this.cancelAction);
  }

  onEmailKeyUp(event: KeyboardEvent) {
    if (event.key === 'Enter' && this.emailControl.valid) {
      this.onPrimaryButtonClicked();
    }
  }
}
